/**
 * EntitiesSummaryTab: one row per entity (companies + personal), YTD
 * revenue, costs, net and tax reserve, with expandable monthly rows.
 *
 * One basis (Fan, 2026-10-25: "check that the overview page ties to all
 * views"). Every figure comes from the SAME endpoints the Entities and
 * Personal P&L pages and the Tax Reserves page read, never a parallel
 * aggregation:
 *   - GET /reports/entity-pnl?scope=entities  -> by_entity rows + ytd total
 *   - GET /reports/entity-pnl?scope=personal  -> by_entity rows + ytd total
 *   - GET /tax-reserves/group-summary (GBP)   -> tax reserve per entity
 * So the Companies total here IS the Entities P&L YTD, the Personal total IS
 * the Personal P&L YTD, and the tax column IS the Tax Reserves page.
 * (It used to read /reports/consolidated, a bank-transaction basis that
 * matched no other page and showed a red "parity mismatch" banner.)
 *
 * Amounts arrive in GBP; the EUR toggle converts with the response's own
 * fx_rates_used via the shared convertAmount helper.
 *
 * Uses function Foo(props) with body destructuring per 147f605 guard.
 */

import { useState, useEffect, useMemo, Fragment } from 'react';
import { Loader2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import { CurrencyToggle } from '../../CurrencyToggle';
import { reportsAPI } from '../../../lib/api';
import { taxReservesGroupAPI } from '../../../lib/api/taxReserves';
import { usePortfolioPrefsStore } from '../../../store';
import { convertAmount } from '../../../lib/currencyConvert';
import { toast } from 'sonner';
import { getErrorMessage } from '../../../lib/errors';

var MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

var COLUMNS = [
  { key: 'name', label: 'Entity', align: 'left' },
  { key: 'revenue', label: 'Revenue YTD', align: 'right' },
  { key: 'costs', label: 'Costs YTD', align: 'right' },
  { key: 'net', label: 'Net YTD', align: 'right' },
  { key: 'tax', label: 'Tax reserve', align: 'right' },
];

function colorClass(v) {
  var n = parseFloat(v);
  if (isNaN(n) || n === 0) return '';
  return n > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
}

function num(v) {
  var n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

// Tax reserve per entity in GBP from /tax-reserves/group-summary: corp tax
// (or personal income tax) on actual months, plus VAT where registered.
// br_ltd reports its BR OS ledger already converted (in_display_currency).
export function taxReserveByEntityGbp(summary) {
  var out = {};
  var rates = (summary && summary.fx_rates_used) || {};
  ((summary && summary.jurisdictions) || []).forEach(function (j) {
    (j.entities || []).forEach(function (r) {
      if (r.in_display_currency) {
        out[r.entity_id] = num(r.in_display_currency.corp_tax_reserve) + num(r.in_display_currency.vat_reserve);
        return;
      }
      var native = num(r.corp_tax_reserve) + num(r.vat_reserve);
      var rate = r.currency && r.currency !== 'GBP' ? num(rates[r.currency]) : 1;
      out[r.entity_id] = native * (rate || 0);
    });
  });
  return out;
}

export function buildSummaryRows(entitiesPnl, personalPnl, taxByEntity) {
  var rows = [];
  function add(group, r) {
    rows.push({
      id: r.entity_id,
      name: r.name,
      group: group,
      revenue: num(r.revenue),
      costs: num(r.costs),
      net: num(r.net_result),
      tax: taxByEntity[r.entity_id] != null ? taxByEntity[r.entity_id] : null,
      nativeCurrency: r.native_currency,
      months: r.months || [],
    });
  }
  ((entitiesPnl && entitiesPnl.by_entity) || []).forEach(function (r) { add('Companies', r); });
  ((personalPnl && personalPnl.by_entity) || []).forEach(function (r) { add('Personal', r); });
  return rows;
}

export function EntitiesSummaryTab() {
  var entState = useState(null);
  var entitiesPnl = entState[0];
  var setEntitiesPnl = entState[1];
  var perState = useState(null);
  var personalPnl = perState[0];
  var setPersonalPnl = perState[1];
  var taxState = useState(null);
  var taxSummary = taxState[0];
  var setTaxSummary = taxState[1];
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  var sortState = useState({ key: 'revenue', asc: false });
  var sort = sortState[0];
  var setSort = sortState[1];
  var expandedState = useState(null);
  var expandedId = expandedState[0];
  var setExpandedId = expandedState[1];

  var year = new Date().getFullYear();
  var displayCurrency = usePortfolioPrefsStore(function (s) { return s.displayCurrency; });
  var fxRates = entitiesPnl ? entitiesPnl.fx_rates_used : null;

  var fmt = function (v) {
    if (v == null) return '—';
    var n = parseFloat(v);
    if (isNaN(n)) return '—';
    var converted = convertAmount(n, 'GBP', displayCurrency, fxRates);
    var sym = displayCurrency === 'EUR' ? '€' : '£';
    var sign = converted < 0 ? '-' : '';
    return sign + sym + Math.abs(converted).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  var fetchData = function () {
    setLoading(true);
    Promise.allSettled([
      reportsAPI.getEntityPnl({ scope: 'entities', year: year }),
      reportsAPI.getEntityPnl({ scope: 'personal', year: year }),
      taxReservesGroupAPI.getGroupSummary(year, 'GBP'),
    ]).then(function (results) {
      if (results[0].status === 'fulfilled') setEntitiesPnl(results[0].value.data);
      else toast.error(getErrorMessage(results[0].reason));
      if (results[1].status === 'fulfilled') setPersonalPnl(results[1].value.data);
      if (results[2].status === 'fulfilled') setTaxSummary(results[2].value.data);
    }).finally(function () { setLoading(false); });
  };

  useEffect(function () { fetchData(); }, []);

  var taxByEntity = useMemo(function () { return taxReserveByEntityGbp(taxSummary); }, [taxSummary]);
  var rows = useMemo(function () {
    return buildSummaryRows(entitiesPnl, personalPnl, taxByEntity);
  }, [entitiesPnl, personalPnl, taxByEntity]);

  var sorted = useMemo(function () {
    var arr = rows.slice();
    var key = sort.key;
    var asc = sort.asc;
    arr.sort(function (a, b) {
      if (a.group !== b.group) return a.group === 'Companies' ? -1 : 1;
      var av = a[key];
      var bv = b[key];
      if (typeof av === 'string') {
        return asc ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return asc ? (av || 0) - (bv || 0) : (bv || 0) - (av || 0);
    });
    return arr;
  }, [rows, sort]);

  var handleSort = function (key) {
    setSort(function (prev) {
      return { key: key, asc: prev.key === key ? !prev.asc : false };
    });
  };

  var sortIcon = function (key) {
    if (sort.key !== key) return null;
    return sort.asc
      ? <ChevronUp className="inline h-3 w-3 ml-0.5" />
      : <ChevronDown className="inline h-3 w-3 ml-0.5" />;
  };

  if (loading && !entitiesPnl) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  var entYtd = (entitiesPnl && entitiesPnl.ytd) || {};
  var perYtd = (personalPnl && personalPnl.ytd) || {};
  var totalRevenue = num(entYtd.revenue) + num(perYtd.revenue);
  var totalCosts = num(entYtd.costs) + num(perYtd.costs);
  var totalNet = num(entYtd.net_result) + num(perYtd.net_result);
  var totalTax = rows.reduce(function (s, r) { return s + (r.tax || 0); }, 0);
  var rowsNet = rows.reduce(function (s, r) { return s + r.net; }, 0);
  var internalRent = rowsNet - totalNet;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Entity Summary</h2>
          <p className="text-sm text-muted-foreground">
            YTD {year}, same figures as the Entities and Personal P&amp;L pages. Amounts in {displayCurrency}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CurrencyToggle />
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={'h-4 w-4 mr-2' + (loading ? ' animate-spin' : '')} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Revenue</p>
            <p className="text-lg font-bold font-mono" data-testid="summary-revenue">{fmt(totalRevenue)}</p>
            <p className="text-[11px] text-muted-foreground">Companies {fmt(entYtd.revenue)} · Personal {fmt(perYtd.revenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Costs</p>
            <p className="text-lg font-bold font-mono">{fmt(totalCosts)}</p>
            <p className="text-[11px] text-muted-foreground">Companies {fmt(entYtd.costs)} · Personal {fmt(perYtd.costs)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Net</p>
            <p className={'text-lg font-bold font-mono ' + colorClass(totalNet)} data-testid="summary-net">{fmt(totalNet)}</p>
            <p className="text-[11px] text-muted-foreground">Companies {fmt(entYtd.net_result)} · Personal {fmt(perYtd.net_result)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Tax reserve</p>
            <p className="text-lg font-bold font-mono">{fmt(totalTax)}</p>
            <p className="text-[11px] text-muted-foreground">Same as the Tax Reserves page</p>
          </CardContent>
        </Card>
      </div>

      {Math.abs(internalRent) >= 1 && (
        <p className="text-xs text-muted-foreground">
          The rows add up to {fmt(rowsNet)} net, {fmt(Math.abs(internalRent))} {internalRent < 0 ? 'less' : 'more'} than
          the total: rent one of our companies pays another is a cost in the paying company&apos;s row and internal in the total.
        </p>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                {COLUMNS.map(function (col) {
                  return (
                    <TableHead
                      key={col.key}
                      className={'cursor-pointer select-none ' + (col.align === 'right' ? 'text-right' : '')}
                      onClick={function () { handleSort(col.key); }}
                    >
                      {col.label}{sortIcon(col.key)}
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map(function (row) {
                var isExpanded = expandedId === row.id;
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={function () { setExpandedId(isExpanded ? null : row.id); }}
                    >
                      <TableCell className="w-8 px-3">
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.name}
                        <Badge variant="outline" className="ml-2 text-[10px] px-1.5 py-0">{row.group}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{fmt(row.revenue)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(row.costs)}</TableCell>
                      <TableCell className={'text-right font-mono font-semibold ' + colorClass(row.net)}>{fmt(row.net)}</TableCell>
                      <TableCell className="text-right font-mono">{fmt(row.tax)}</TableCell>
                    </TableRow>
                    {isExpanded && row.months.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={COLUMNS.length + 1} className="p-0 bg-muted/30">
                          <div className="px-6 py-3">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Month</TableHead>
                                  <TableHead className="text-right">Revenue</TableHead>
                                  <TableHead className="text-right">Costs</TableHead>
                                  <TableHead className="text-right">Net</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {row.months.map(function (m) {
                                  var mi = parseInt(String(m.month).slice(5, 7), 10) - 1;
                                  return (
                                    <TableRow key={m.month}>
                                      <TableCell className="text-sm">{MONTH_LABELS[mi] || m.month}</TableCell>
                                      <TableCell className="text-right font-mono text-sm">{fmt(m.revenue)}</TableCell>
                                      <TableCell className="text-right font-mono text-sm">{fmt(m.costs)}</TableCell>
                                      <TableCell className={'text-right font-mono text-sm font-semibold ' + colorClass(m.net_result)}>{fmt(m.net_result)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {sorted.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 1} className="text-center text-muted-foreground py-8">
                    No entity data available.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
